/* eslint-disable max-lines */
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { OfferType } from '@smg-real-estate/hg-rets-schema';
import {
  Availability,
  ChooseType,
  extractUrlQueryParts,
  Facility,
  ListingCardSize,
  ListingItem,
  SearchModel,
  SearchResult,
  SortDirection,
  SortType,
} from '@smg-real-estate/hg-search-libs';

import {
  GTMPageType,
  HarmonizedTracking,
  InteractionContainerDetail,
  InteractionContainerName,
  InteractionEvent,
} from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';

type PageType = InteractionContainerName.MAP | InteractionContainerDetail.LIST;

export const getListingPosition = (
  listing: ListingItem,
  listings: ListingItem[],
): number | undefined => {
  const position = listings.indexOf(listing);
  return position > -1 ? position + 1 : undefined;
};

export const getSortedDetails = (
  sortDirection: SortDirection,
  sortType: SortType,
): SortType.topListing | `${SortDirection}-${SortType}` => {
  if (sortType === SortType.topListing) return SortType.topListing;
  return `${sortDirection}-${sortType.toLowerCase()}` as `${SortDirection}-${SortType}`;
};

export const isShowAgencyAgent = (listing: ListingItem): boolean =>
  !!listing.agencyAgent &&
  !!listing.listerBranding?.isPremiumBranding &&
  listing.listingCard !== undefined &&
  listing.listingCard.size !== ListingCardSize.SMALL &&
  listing.listing.offerType === OfferType.Buy;

const convertListingTypeToValuesForTracking = (type: any): any => {
  return type || 'unknown';
};

export const generateResultListPageTrackingContext = async (
  language: HgSupportedLanguage,
  searchModel: SearchModel,
  result: SearchResult,
  pageType: PageType = InteractionContainerDetail.LIST,
): Promise<HarmonizedTracking> => {
  const vasDisplayed = result.listings.map((l) =>
    convertListingTypeToValuesForTracking(l.listingType?.type),
  );
  const searchQueryMeta = generateSearchQueryMetaDataHarmonized(searchModel);

  return {
    ...searchQueryMeta,
    h_srp_items_loaded_078: result.listings.map((listing) => listing.id),
    h_srp_items_vas_displayed_079: vasDisplayed,
    h_search_term_input_081: searchModel.text?.query,
    h_page_type_110: GTMPageType.RESULT_LIST_PAGE,
    h_list_type_111: pageType,
    h_content_language_116: language,
    h_search_results_loaded_279: result.listings.length,
    h_search_results_total_280: result.resultCount,
    h_search_vas_displayed_282: vasDisplayed.length,
    h_search_results_pages_all_287: result.pageCount,
    h_search_results_page_current_288: result.page,
    h_search_results_page_size_289: result.itemsPerPage,
    h_srp_items_agency_pic_177: result.listings.map((listing) =>
      isShowAgencyAgent(listing) ? 1 : 0,
    ),
    h_search_agency_pic_loaded_178: result.listings.reduce(
      (count, listing) => (isShowAgencyAgent(listing) ? count + 1 : count),
      0,
    ),
    ...(typeof window !== 'undefined' &&
      RegExp(/expired=(\S+)/).exec(window.location.search) && {
        h_context_060: 'pdp_not_found_redirect',
      }),
    h_experiment_variant_array_048: 'RBD-1775=on',
  };
};

export const trackResultListVirtualPageView = async (
  language: HgSupportedLanguage,
  searchModel: SearchModel,
  result: SearchResult,
  pageType: PageType = InteractionContainerDetail.LIST,
  isPolygonSearch = false,
  // eslint-disable-next-line max-params
): Promise<void> => {
  const trackingContext = await generateResultListPageTrackingContext(
    language,
    searchModel,
    result,
    pageType,
  );

  trackEventHarmonized(InteractionEvent.VIRTUAL_PAGEVIEW, {
    ...trackingContext,
    ...(isPolygonSearch ? { h_search_source_072: InteractionContainerDetail.POLYGON } : {}),
  });
};

export function trackSearchSubmit(
  searchModel: SearchModel,
  pageType: GTMPageType,
  action: 'search' | 'changeSearch',
): void {
  const searchQueryMetaDataHarmonized = generateSearchQueryMetaDataHarmonized(searchModel);

  trackEventHarmonized(InteractionEvent.SUBMIT_SEARCH, {
    h_event_category_006: InteractionEvent.SUBMIT_SEARCH,
    h_event_action_007: `${pageType}_${action}`,
    h_event_label_008: searchModel.locations.join(','),
    h_search_source_072: pageType,
    h_search_term_input_081: searchModel.text?.query,
    ...searchQueryMetaDataHarmonized,
  });
}

// ============ HELPER FUNCTIONS FOR ACTIVE FUNCTIONS ============

const offerTypeFrom = (chooseType: ChooseType | undefined) =>
  chooseType ? (chooseType.toString().startsWith('rent') ? 'rent' : 'buy') : '';

type OneOrUndefined = 1 | undefined;

export interface SearchQueryMetaDataHarmonized {
  h_search_term_geo_080?: string;
  h_number_of_search_terms_entered_278?: number;
  h_search_offer_type_083?: 'buy' | 'rent' | '';
  h_search_filter_category_075?: string;
  h_search_filter_category_sub_084?: string;
  h_search_filter_082?: string;
  h_sf_price_from_340?: number;
  h_sf_price_to_341?: number;
  h_sf_rooms_from_342?: number;
  h_sf_rooms_to_343?: number;
  h_sf_radius_354?: number;
  h_sf_livingspace_from_344?: number;
  h_sf_livingspace_to_345?: number;
  h_sf_lotsizefrom_346?: number;
  h_sf_lotsizeto_347?: number;
  h_search_filter_floortype_068?: 'ground_floor' | 'not_ground_floor';
  h_search_filter_available_from_069?: string;
  h_search_sorted_details_073?: string;
  h_sf_floorspace_from_348?: number;
  h_sf_floorspace_to_349?: number;
  h_sf_cubature_from_350?: number;
  h_sf_cubature_to_351?: number;
  h_sf_buildyear_from_352?: number;
  h_sf_buildyear_to_353?: number;
  h_sf_only_with_price_362?: OneOrUndefined;
  h_sf_facilities_isfamilyfriendly_355?: OneOrUndefined;
  h_sf_facilities_hasbalconyorpatio_356?: OneOrUndefined;
  h_sf_facilities_petsallowed_357?: OneOrUndefined;
  h_sf_facilities_isminergie_358?: OneOrUndefined;
  h_sf_facilities_haselevator_359?: OneOrUndefined;
  h_sf_facilities_haswheelchairsaccess_360?: OneOrUndefined;
  h_sf_facilities_isnewbuilding_361?: OneOrUndefined;
  h_sf_facilities_isoldbuilding_364?: OneOrUndefined;
  h_sf_facilities_hasparking_363?: OneOrUndefined;
  h_sf_facilities_haspool_365?: OneOrUndefined;
}

const booleanToOneOrUndefined = (b: boolean | undefined): OneOrUndefined => (b ? 1 : undefined);

const requiresFacility = (searchModel: SearchModel, facility: Facility): OneOrUndefined =>
  booleanToOneOrUndefined(searchModel.facilitiesRequired.includes(facility));

const generateSearchQueryMetaDataHarmonized = (
  searchModel: SearchModel,
): SearchQueryMetaDataHarmonized => {
  const searchFilterString = extractUrlQueryParts(searchModel);

  return {
    h_search_term_geo_080: searchModel.locations.join(','),
    h_number_of_search_terms_entered_278: searchModel.locations.length,
    h_search_offer_type_083: offerTypeFrom(searchModel.chooseType),
    h_search_filter_category_075: searchModel.chooseType?.toString(),
    h_search_filter_category_sub_084: searchModel.objectTypes.join(',') || undefined,
    h_search_filter_082: searchFilterString.filterQuery,
    h_sf_price_from_340: searchModel.priceRange?.from,
    h_sf_price_to_341: searchModel.priceRange?.to,
    h_sf_rooms_from_342: searchModel.numberOfRooms?.from,
    h_sf_rooms_to_343: searchModel.numberOfRooms?.to,
    h_sf_radius_354: searchModel.radius,
    h_sf_livingspace_from_344: searchModel.surfaceLivingRange?.from,
    h_sf_livingspace_to_345: searchModel.surfaceLivingRange?.to,
    h_sf_lotsizefrom_346: searchModel.surfacePropertyRange?.from,
    h_sf_lotsizeto_347: searchModel.surfacePropertyRange?.to,
    h_search_filter_floortype_068: searchModel.floorType,
    h_search_filter_available_from_069: convertAvailabilityForTracking(searchModel.availability),
    h_search_sorted_details_073: getSortedDetails(searchModel.sortDirection, searchModel.sortType),
    h_sf_floorspace_from_348: searchModel.surfaceUsableRange?.from,
    h_sf_floorspace_to_349: searchModel.surfaceUsableRange?.to,
    h_sf_cubature_from_350: searchModel.volumeRange?.from,
    h_sf_cubature_to_351: searchModel.volumeRange?.to,
    h_sf_buildyear_from_352: searchModel.buildYearRange?.from,
    h_sf_buildyear_to_353: searchModel.buildYearRange?.to,
    h_sf_only_with_price_362: booleanToOneOrUndefined(searchModel.isPriceDefined),
    h_sf_facilities_isfamilyfriendly_355: requiresFacility(searchModel, Facility.FAMILY_FRIENDLY),
    h_sf_facilities_hasbalconyorpatio_356: requiresFacility(searchModel, Facility.HAS_BALCONY),
    h_sf_facilities_petsallowed_357: requiresFacility(searchModel, Facility.ANIMALS_ALLOWED),
    h_sf_facilities_isminergie_358: requiresFacility(searchModel, Facility.IS_MINERGIE),
    h_sf_facilities_haselevator_359: requiresFacility(searchModel, Facility.HAS_ELEVATOR),
    h_sf_facilities_haswheelchairsaccess_360: requiresFacility(
      searchModel,
      Facility.WHEELCHAIRS_ALLOWED,
    ),
    h_sf_facilities_isnewbuilding_361: requiresFacility(searchModel, Facility.NEWLY_BUILT),
    h_sf_facilities_isoldbuilding_364: requiresFacility(searchModel, Facility.BUILT_OLD),
    h_sf_facilities_hasparking_363: requiresFacility(searchModel, Facility.HAS_PARKING_OR_GARAGE),
    h_sf_facilities_haspool_365: requiresFacility(searchModel, Facility.HAS_SWIMMING_POOL),
  };
};

function convertAvailabilityForTracking(availability?: Availability): undefined | string {
  if (!availability) {
    return undefined;
  }
  if (availability === Availability.IMMEDIATELY) {
    return 'immediately';
  }
  return `in${availability.toString().toLocaleLowerCase()}onths`;
}

export function trackPaginationClicked(title: string): void {
  trackEventHarmonized(InteractionEvent.GENERIC_INTERACTION, {
    h_event_category_006: 'generic_interaction',
    h_event_action_007: 'sort',
    h_event_label_008: title,
  });
}

export function trackOpenExtendedFilter(): void {
  trackEventHarmonized(InteractionEvent.SEARCH_BAR_INTERACTION, {
    h_event_action_007: 'open_extended_filter',
    h_event_label_008: InteractionContainerName.SEARCH_BAR,
  });
}

export function trackResetFiltersClicked(): void {
  trackEventHarmonized(InteractionEvent.GENERIC_LINK_CLICK, {
    h_event_category_006: 'generic_link_click',
    h_event_action_007: 'clearFilter',
    h_event_label_008: 'all',
  });
}
